import { ref } from 'vue';
import { createI18n } from 'vue-i18n';

import cmn from './messages/cmn.json';
import en from './messages/en.json';
import yue from './messages/yue.json';

// Locale IS the case/voice language, ported as-is from cuhk-osce-simulation's
// src/i18n/locales.ts — one setting drives UI text AND the backend's
// patient/examiner AI responses (see api/osce.ts's `locale` params). No URL
// prefix (this app's route tree doesn't need it) — persisted to
// localStorage instead of the prototype's locale cookie, since there's no
// server-side rendering here to read a cookie on.
export const locales = ['en', 'yue', 'cmn'] as const;
export type Locale = (typeof locales)[number];
export const defaultLocale: Locale = 'en';

const LOCALE_STORAGE_KEY = 'stw-osce-locale';

export function isLocale(value: string | null | undefined): value is Locale {
  return !!value && (locales as readonly string[]).includes(value);
}

function getInitialLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(stored) ? stored : defaultLocale;
  } catch {
    return defaultLocale;
  }
}

export const i18n = createI18n({
  legacy: false,
  locale: getInitialLocale(),
  fallbackLocale: defaultLocale,
  messages: { en, yue, cmn },
});

// True while an OSCE session is running (chat/[caseId].vue sets it) — the
// session's language is fixed at start, so switching mid-session would only
// change UI text while the AI kept replying in the old language.
export const isLocaleLocked = ref(false);

export function setLocale(locale: Locale) {
  if (isLocaleLocked.value) return;
  i18n.global.locale.value = locale;
  applyDocumentLang(locale);
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Private-browsing/blocked storage — locale still applies for this
    // session, it just won't persist across reloads.
  }
}

export function getCurrentLocale(): Locale {
  return i18n.global.locale.value as Locale;
}

// BCP 47 tag for toLocaleDateString()/toLocaleString() in the current UI
// locale — utils/dateFormat.ts's helpers are hard-wired to en-US (the admin
// page relies on that), so user-facing dates format through this instead.
const INTL_LOCALES: Record<Locale, string> = {
  en: 'en-US',
  yue: 'zh-HK',
  cmn: 'zh-CN',
};

// <html lang> picks the CJK font stack in styles/main.scss — left as "en",
// browsers fall back glyph by glyph and Simplified text renders in mixed
// fonts.
function applyDocumentLang(locale: Locale) {
  document.documentElement.lang = INTL_LOCALES[locale];
}
applyDocumentLang(getCurrentLocale());

export function getIntlLocale(): string {
  return INTL_LOCALES[getCurrentLocale()];
}

// "May 6, 2025" / "2025年5月6日", following the UI locale.
export function formatLocaleDate(date: Date | string): string {
  return new Date(date).toLocaleDateString(getIntlLocale(), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
